'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { LoginForm } from '@/components/auth/LoginForm';
import { useLoginHandler } from '@/hooks/useLoginHandler';
import { LoginCredentials, AuthError } from '@/types/auth';
import { LoginSkeleton } from '@/components/skeletons/LoginSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { APP_CONSTANTS } from '../../../../parent-frontend/src/constants/app.constants';
import { nestLogoWithName } from '../../constants/brand/brandLogos';

// Role constants
const USER_ROLES = {
  PARENT: 'PARENT',
} as const;

export default function LoginPage() {
  const router = useRouter();
  const {
    user,
    token,
    isLoading: isAuthLoading,
    logout: clearAuthData,
  } = useAuth();
  const isAuthenticated = !!token && !!user;

  const { loginUser, isLoggingIn, loginError, resetLoginAttempt } =
    useLoginHandler();

  // DEV TODO : The way redirections are handled here might not be correct this should be resolved.
  const mainAppUrl =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:4200'
      : APP_CONSTANTS.APP_URL || 'https://nest.emmple.com';

  const adminAppUrl =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:4200/login'
      : `${APP_CONSTANTS.APP_URL}/login` || 'https://nest.emmple.com/login';

  useEffect(() => {
    if (isAuthLoading) return;
    if (isAuthenticated && user?.role === USER_ROLES.PARENT) {
      // DEV NOTE : we are using this to ensure no admin can login as parent ( roles integrity check)
      // router.replace('/');
    } else if (isAuthenticated && user?.role !== USER_ROLES.PARENT) {
      clearAuthData();
    }
  }, [isAuthenticated, user, router, clearAuthData, isAuthLoading]);

  const queryClient = useQueryClient();

  const handleFormSubmit = async (credentials: LoginCredentials) => {
    try {
        await queryClient.cancelQueries();
    } catch (err) {
      console.warn('[parent-login] Failed to cancel existing queries', err);
    }
    await loginUser(credentials);
  };

  if (isAuthLoading) {
    return <LoginSkeleton />;
  }

  if (loginError instanceof AuthError && loginError.isNetworkError) {
    return (
      <ErrorState
        title="Server Unavailable"
        message={loginError.message}
        onRetry={() => {
          resetLoginAttempt();
        }}
        icon={<AlertCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />}
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-100 p-3 sm:p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-4">
          <a
            href={mainAppUrl}
            className="inline-flex items-center gap-2 text-[#4D4F52] hover:text-[#0D373D] transition-colors"
            aria-label="Go back to homepage"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back</span>
          </a>
        </div>

        {/* Login Card */}
        <div className="rounded-xl bg-white p-6 sm:p-8 shadow-lg">
          {/* Logo */}
          <div className="flex items-center justify-start mb-4">
            <img
              src={nestLogoWithName.src}
              alt={`${APP_CONSTANTS.APP_NAME} Logo`}
              height={24}
              className="h-6 w-auto -ml-1"
            />
          </div>

          {/* Heading */}
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0D373D] mb-2">
            Parent Portal Sign In
          </h2>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-6">
            Access your account to manage your child's details.
          </p>

          {/* Login Form */}
          <LoginForm
            onSubmit={handleFormSubmit}
            isLoading={isLoggingIn}
            errorMessage={loginError ? loginError.message : null}
          />

          {/* Forgot Password - Right Aligned */}
          <div className="mt-4 text-right">
            <a
              href="/parent/forgot-password"
              className="text-xs text-[#0D373D] hover:text-[#0D373D]/80 hover:underline"
            >
              Forgot Password?
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} Nest by Emmple. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-2">
            <div className="h-px w-8 bg-gray-300"></div>
            <a
              href={adminAppUrl}
              className="text-xs text-gray-500 hover:text-[#0D373D] hover:underline"
            >
              Institute Login
            </a>
            <div className="h-px w-8 bg-gray-300"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
